import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '@/components/ui/Logo';
import { MessageSquare, X, Send, Loader2, RefreshCw } from 'lucide-react';
import aaryatiIconPath from '@assets/A.png';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';

type Message = {
  id: string;
  text: string;
  sender: 'bot' | 'user';
  options?: { label: string; value: string }[];
  isTyping?: boolean;
};

type UserData = {
  name: string;
  email: string;
  company: string;
  role: string;
  appSize: string;
  requirements: string;
};

const ChatbotButton = () => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState('INIT');

  // Form data state
  const [userData, setUserData] = useState<UserData>({
    name: '',
    email: '',
    company: '',
    role: '',
    appSize: '',
    requirements: ''
  });

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const toggleChat = () => {
    const newState = !isChatOpen;
    setIsChatOpen(newState);
    setIsTooltipVisible(false);

    // Initialize chat if opening for the first time
    if (newState && messages.length === 0) {
      addBotMessage("Hi there! I'm your Aaryati assistant. Are you looking to modernize your legacy MuleSoft applications?", [
        { label: "Yes, tell me more", value: "YES" },
        { label: "Just browsing", value: "BROWSING" }
      ]);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isChatOpen && inputRef.current && !messages[messages.length - 1]?.options) {
      inputRef.current.focus();
    }
  }, [messages, isChatOpen]);

  // Simulate typing delay
  const addBotMessage = (text: string, options?: { label: string; value: string }[], delay = 1000) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        text,
        sender: 'bot',
        options
      }]);
    }, delay);
  };

  const addUserMessage = (text: string) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text,
      sender: 'user'
    }]);
  };

  const handleOptionClick = (value: string, label: string) => {
    addUserMessage(label);
    processStep(value);
  };

  const handleInputSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputMessage.trim()) return;

    const text = inputMessage;
    setInputMessage('');
    addUserMessage(text);
    processStep(text);
  };

  const processStep = async (input: string) => {
    // Process based on current step
    switch (currentStep) {
      case 'INIT':
        if (input === 'YES') {
          setCurrentStep('ASK_SIZE');
          addBotMessage("Great! Moving to Spring Boot can significantly reduce operational costs. How large is your current MuleSoft landscape?", [
            { label: "1-10 APIs", value: "1-10" },
            { label: "11-50 APIs", value: "11-50" },
            { label: "50+ APIs", value: "50+" }
          ]);
        } else {
          setCurrentStep('BROWSING');
          addBotMessage("No problem! Feel free to explore our Resources section for guides and case studies. Let me know if you change your mind!", [
            { label: "Start Over", value: "RESTART" }
          ]);
        }
        break;

      case 'BROWSING':
        if (input === 'RESTART') {
          setMessages([]);
          setCurrentStep('INIT');
          addBotMessage("Hi there! I'm your Aaryati assistant. Are you looking to modernize your legacy MuleSoft applications?", [
            { label: "Yes, tell me more", value: "YES" },
            { label: "Just browsing", value: "BROWSING" }
          ]);
        }
        break;

      case 'ASK_SIZE':
        setUserData(prev => ({ ...prev, appSize: input }));
        setCurrentStep('ASK_NAME');
        addBotMessage("Thanks! We have specialized analyzers for that scale. To send you a custom migration report, may I have your name?");
        break;

      case 'ASK_NAME':
        setUserData(prev => ({ ...prev, name: input }));
        setCurrentStep('ASK_EMAIL');
        addBotMessage(`Nice to meet you, ${input}. What is your work email address?`);
        break;

      case 'ASK_EMAIL':
        if (!validateEmail(input)) {
          addBotMessage("That doesn't look like a valid email. Please try again.");
          return;
        }
        setUserData(prev => ({ ...prev, email: input }));
        setCurrentStep('ASK_COMPANY');
        addBotMessage("Got it. And what is your company name?");
        break;

      case 'ASK_COMPANY':
        setUserData(prev => ({ ...prev, company: input }));
        setCurrentStep('ASK_ROLE');
        addBotMessage("Thanks. Lastly, what is your role there?");
        break;

      case 'ASK_ROLE':
        setUserData(prev => ({ ...prev, role: input }));
        setCurrentStep('ASK_REQ');
        addBotMessage("Any specific challenges or requirements you'd like us to know about?", [
          { label: "No specific requirements", value: "None" }
        ]);
        break;

      case 'ASK_REQ':
        const finalData = { ...userData, requirements: input };
        setUserData(finalData);
        setCurrentStep('SUBMITTING');
        addBotMessage("Perfect. I'm submitting your details to our engineering team now...");

        try {
          // API Call
          await axios.post('/api/submit-enquiry', finalData);

          setCurrentStep('DONE');
          addBotMessage("Done! We've received your request and will be in touch shortly with your migration assessment. Have a great day!", [
            { label: "Start New Conversation", value: "RESTART" }
          ]);
        } catch (error) {
          console.error("Submission error", error);
          setCurrentStep('DONE'); // Still show done to user but maybe with a different message or retry?
          addBotMessage("I'm having trouble connecting to the server. Please try again later or contact us directly at contact@aaryati.com.", [
            { label: "Try Again", value: "RETRY_SUBMIT" }
          ]);
        }
        break;

      case 'DONE':
        if (input === 'RESTART') {
          setMessages([]);
          setUserData({ name: '', email: '', company: '', role: '', appSize: '', requirements: '' });
          setCurrentStep('INIT');
          addBotMessage("Hi there! I'm your Aaryati assistant. Are you looking to modernize your legacy MuleSoft applications?", [
            { label: "Yes, tell me more", value: "YES" },
            { label: "Just browsing", value: "BROWSING" }
          ]);
        } else if (input === 'RETRY_SUBMIT') {
          // Retry submission logic
          setCurrentStep('ASK_REQ');
          processStep(userData.requirements);
        }
        break;
    }
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      <AnimatePresence>
        {isTooltipVisible && !isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            className="absolute bottom-[80px] right-0 bg-white text-slate-900 p-4 rounded-lg shadow-lg max-w-xs border border-slate-100"
            style={{ transformOrigin: 'bottom right' }}
          >
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-1 text-sm">Have questions?</p>
                <p className="text-xs text-slate-500">Chat with Aaryati Assistant about your migration needs.</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setIsTooltipVisible(false); }}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="absolute bottom-[-8px] right-6 w-4 h-4 bg-white transform rotate-45 border-r border-b border-slate-100"></div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="absolute bottom-[80px] right-0 bg-white rounded-xl shadow-2xl overflow-hidden w-[350px] sm:w-[380px] border border-slate-200 flex flex-col max-h-[600px]"
            style={{ transformOrigin: 'bottom right' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 shadow-md">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-1.5 rounded-full backdrop-blur-sm">
                    <Logo variant="icon" className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Aaryati Assistant</h3>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <p className="text-[10px] text-blue-100 font-medium">Online</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setMessages([]); setCurrentStep('INIT'); toggleChat(); toggleChat(); }}
                    className="text-blue-100 hover:text-white p-1 hover:bg-white/10 rounded-full transition-colors"
                    title="Restart Chat"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button
                    onClick={toggleChat}
                    className="text-blue-100 hover:text-white p-1 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div
              ref={chatContainerRef}
              className="flex-1 bg-slate-50 p-4 overflow-y-auto min-h-[300px]"
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`mb-4 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-tr-none shadow-md'
                      : 'bg-white text-slate-800 rounded-tl-none border border-slate-100 shadow-sm'
                      }`}
                  >
                    <p>{msg.text}</p>

                    {/* Render Options if available */}
                    {msg.sender === 'bot' && msg.options && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {msg.options.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleOptionClick(option.value, option.label)}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs px-3 py-1.5 rounded-full border border-blue-200 transition-colors"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start mb-4">
                  <div className="bg-white text-slate-800 p-3 rounded-2xl rounded-tl-none border border-slate-100 shadow-sm">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleInputSubmit} className="p-3 bg-white border-t border-slate-100">
              <div className="relative flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your message..."
                  disabled={(messages[messages.length - 1]?.options !== undefined && currentStep !== 'ASK_REQ') || currentStep === 'DONE' || currentStep === 'SUBMITTING'}
                  className="flex-1 pl-4 pr-10 py-2.5 rounded-full border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm bg-slate-50 focus:bg-white text-slate-900 placeholder:text-slate-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!inputMessage.trim() || (messages[messages.length - 1]?.options !== undefined && currentStep !== 'ASK_REQ') || currentStep === 'DONE' || currentStep === 'SUBMITTING'}
                  className="rounded-full h-10 w-10 bg-blue-600 hover:bg-blue-700 shrink-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating chat button */}
      <motion.button
        className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center h-14 w-14 md:h-16 md:w-16 relative overflow-hidden transition-all hover:shadow-blue-500/30 ring-4 ring-white/20"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleChat}
        onMouseEnter={() => !isChatOpen && setIsTooltipVisible(true)}
        onMouseLeave={() => setIsTooltipVisible(false)}
      >
        <div className="flex items-center justify-center w-full h-full p-2.5">
          {isChatOpen ? (
            <X className="h-6 w-6 md:h-7 md:w-7" />
          ) : (
            <div className="bg-white rounded-full w-full h-full flex items-center justify-center p-2 shadow-sm">
              <img
                src={aaryatiIconPath}
                alt="Aaryati Chat"
                className="h-full w-full object-contain"
              />
            </div>
          )}
        </div>

        {/* Notification badge */}
        {!isChatOpen && messages.length === 0 && (
          <span className="absolute top-3 right-3 md:top-4 md:right-4 w-3 h-3 bg-red-500 border-2 border-blue-600 rounded-full"></span>
        )}
      </motion.button>
    </div>
  );
};

export default ChatbotButton;